import { useEffect, useRef, useState } from 'react';

import type {
  FieldValue,
  FilterValues,
} from '@src/components/FilterDropDown/FilterDropDown.interfaces';

import { get, set } from '../sessionStorage';
import { getViewSlice, setViewSlice } from '../sessionStorage/viewState';

export type NormalizeStoredFilters = (
  stored: unknown,
  defaults: FilterValues,
) => FilterValues;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const isFieldValue = (value: unknown): value is FieldValue =>
  value === null ||
  value === undefined ||
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean' ||
  (Array.isArray(value) && value.every(item => typeof item === 'string'));

export const mergeStoredFilters = (
  stored: unknown,
  defaults: FilterValues,
): FilterValues => {
  if (!isPlainObject(stored)) return defaults;

  return Object.fromEntries(
    Object.entries(defaults).map(([key, fieldDefaults]) => {
      const storedField = stored[key];
      if (!isPlainObject(storedField)) {
        return [key, fieldDefaults];
      }

      const nextField = { ...fieldDefaults };
      if (isFieldValue(storedField.value)) {
        nextField.value = storedField.value;
      }
      if (typeof storedField.valueTranslated === 'string') {
        nextField.valueTranslated = storedField.valueTranslated;
      }

      return [key, nextField];
    }),
  );
};

/**
 * The SecondaryPanel provider holds no values until `SecondaryPanel.Panel`
 * mounts with `initialValues`, so on every remount the panel reports an empty
 * object for a few renders. Lists must treat that window as "not hydrated yet"
 * instead of as a filter change.
 */
export const isFilterPanelHydrated = (values: FilterValues): boolean =>
  Object.keys(values).length > 0;

type UsePersistedFiltersParams = {
  defaults: FilterValues;
  values: FilterValues;
  storageKey?: string | null;
  viewStorageKey?: string | null;
  normalize?: NormalizeStoredFilters;
  /** Applied only when writing to sessionStorage (not on read). */
  transformBeforePersist?: (values: FilterValues) => FilterValues;
};

const readStoredFilters = ({
  storageKey,
  viewStorageKey,
}: Pick<UsePersistedFiltersParams, 'storageKey' | 'viewStorageKey'>): unknown => {
  if (viewStorageKey) return getViewSlice(viewStorageKey, 'filters') ?? null;
  if (storageKey) return get(storageKey, null);
  return null;
};

const readInitialFilters = ({
  storageKey,
  viewStorageKey,
  defaults,
  normalize,
}: Pick<
  UsePersistedFiltersParams,
  'storageKey' | 'viewStorageKey' | 'defaults' | 'normalize'
>): FilterValues => {
  if (!storageKey && !viewStorageKey) return defaults;
  const stored = readStoredFilters({ storageKey, viewStorageKey });
  if (normalize) return normalize(stored, defaults);
  return mergeStoredFilters(stored, defaults);
};

/**
 * Persists SecondaryPanel filter values in sessionStorage.
 * `viewStorageKey` writes the `filters` slice of a per-view blob.
 * `storageKey` is the legacy full-document key.
 * Reads once on mount; writes on later `values` changes (skips the first render).
 */
const usePersistedFilters = ({
  storageKey,
  viewStorageKey,
  defaults,
  values,
  normalize,
  transformBeforePersist,
}: UsePersistedFiltersParams) => {
  const [initialFilters] = useState(() =>
    readInitialFilters({ storageKey, viewStorageKey, defaults, normalize }),
  );

  const skipNextWrite = useRef(true);

  useEffect(() => {
    if (!storageKey && !viewStorageKey) return;
    if (skipNextWrite.current) {
      skipNextWrite.current = false;
      return;
    }
    const payload = transformBeforePersist ? transformBeforePersist(values) : values;

    if (viewStorageKey) {
      setViewSlice(viewStorageKey, 'filters', payload);
      return;
    }
    if (storageKey) set(storageKey, payload);
  }, [storageKey, viewStorageKey, transformBeforePersist, values]);

  return { initialFilters };
};

export default usePersistedFilters;
